import type { IPatient } from '@app/domain/patient/patient.interface'
import type { IVisit } from '@app/domain/visit/visit.interface'
import type { AppAction } from '@app/state'

import type { IApiNewPatient, IApiUpdatePatient } from './helpers'

export interface ICurrentPatient {
  info: IPatient
  visits?: Array<IVisit>
}

export const enum PatientActionStatusId {
  Pending = 'pending',
  Succeeded = 'succeeded',
  Failed = 'failed',
}

export enum SearchStatus {
  PENDING = 'pending',
  FAILED = 'failed',
  FOUND = 'found',
  NOT_FOUND = 'not-found',
}

export interface State {
  list: Array<IPatient>
  searchStatus?: SearchStatus
  createPatientErrorMessage: string
  currentPatient: ICurrentPatient | undefined
  patientAction?: {
    status: PatientActionStatusId
  }
}

export enum ActionType {
  ClearPatientContext = 'PATIENTS/CLEAR_CONTEXT',
  ClearActionStatus = 'PATIENTS/CLEAR_ACTION_STATUS',
  ClearErrorMessage = 'PATIENTS/CLEAR_ERROR_MESSAGE',
  GetList = 'PATIENTS/GET_PATIENTS',
  ListRetrieved = 'PATIENTS/LIST_RETRIEVED',
  GetPatient = 'PATIENTS/GET_PATIENT',
  PatientRetrieved = 'PATIENTS/PATIENT_RETRIEVED',
  GetPatientVisits = 'PATIENTS/GET_PATIENT_VISITS',
  PatientVisitsRetrieved = 'PATIENTS/PATIENT_VISITS_RETRIEVED',
  GetPatientWithVisits = 'PATIENTS/GET_PATIENT_WITH_VISITS',
  // Create Patient Actions
  CreatePatient = 'PATIENTS/CREATE',
  BeginCreatePatient = 'PATIENTS/CREATE/BEGIN',
  CreatePatientSucceeded = 'PATIENTS/CREATE/SUCCEEDED',
  CreatePatientFailed = 'PATIENTS/CREATE/FAILED',

  UpdatePatient = 'PATIENTS/UPDATE',
  UpdatePatientBegin = 'PATIENTS/UPDATE/BEGIN',
  UpdatePatientSuccess = 'PATIENTS/UPDATE/SUCCESS',
  UpdatePatientFailure = 'PATIENTS/UPDATE/FAILURE',

  FindPatientByMRN = 'PATIENTS/FIND_BY_MRN',
  BeginFindPatientByMRN = 'PATIENTS/FIND_BY_MRN/BEGIN',
  FindPatientByMRNFailed = 'PATIENTS/FIND_BY_MRN/FAILED',
  FindPatientByMRNSucceeded = 'PATIENTS/FIND_BY_MRN/SUCCEEDED',
  NoPatientFound = 'PATIENTS/NO_PATIENT_FOUND',
}

export type Action =
  | { type: ActionType.ClearPatientContext }
  | { type: ActionType.ClearActionStatus }
  | { type: ActionType.ClearErrorMessage }
  | { type: ActionType.GetList }
  | { type: ActionType.ListRetrieved; list: Array<IPatient> }
  | { type: ActionType.GetPatient; patientId: string }
  | { type: ActionType.PatientRetrieved; patient: IPatient }
  | { type: ActionType.GetPatientVisits; patientId: string }
  | { type: ActionType.PatientVisitsRetrieved; visits: Array<IVisit> }
  | { type: ActionType.GetPatientWithVisits; patientId: string }
  | { type: ActionType.CreatePatient; patient: IApiNewPatient }
  | { type: ActionType.BeginCreatePatient }
  | { type: ActionType.CreatePatientSucceeded; patient: IPatient }
  | { type: ActionType.CreatePatientFailed; errorMessage: string }
  | { type: ActionType.UpdatePatient; id: string; patient: IApiUpdatePatient }
  | { type: ActionType.UpdatePatientBegin }
  | { type: ActionType.UpdatePatientSuccess; patient: IPatient }
  | { type: ActionType.UpdatePatientFailure }
  | { type: ActionType.FindPatientByMRN; mrn: string }
  | { type: ActionType.BeginFindPatientByMRN }
  | { type: ActionType.FindPatientByMRNFailed; error: string }
  | { type: ActionType.FindPatientByMRNSucceeded; patient: IPatient }
  | { type: ActionType.NoPatientFound }

export const actions = {
  clearPatientContext(): Action {
    return { type: ActionType.ClearPatientContext }
  },
  clearActionStatus(): Action {
    return { type: ActionType.ClearActionStatus }
  },
  clearErrorMessage(): Action {
    return { type: ActionType.ClearErrorMessage }
  },
  getList(): Action {
    return { type: ActionType.GetList }
  },
  listRetrieved(patients: Array<IPatient>): Action {
    return { type: ActionType.ListRetrieved, list: patients }
  },
  getPatient(id: string): Action {
    return { type: ActionType.GetPatient, patientId: id }
  },
  patientRetrieved(patient: IPatient): Action {
    return { type: ActionType.PatientRetrieved, patient }
  },
  getPatientVisits(id: string): Action {
    return { type: ActionType.GetPatientVisits, patientId: id }
  },
  patientVisitsRetrieved(visits: Array<IVisit>): Action {
    return { type: ActionType.PatientVisitsRetrieved, visits }
  },
  getPatientWithVisits(id: string): Action {
    return { type: ActionType.GetPatientWithVisits, patientId: id }
  },
  createPatient(patient: IApiNewPatient): Action {
    return { type: ActionType.CreatePatient, patient }
  },
  beginCreatePatient(): Action {
    return { type: ActionType.BeginCreatePatient }
  },
  createPatientSucceeded(patient: IPatient): Action {
    return { type: ActionType.CreatePatientSucceeded, patient }
  },
  createPatientFailed(errorMessage: string): Action {
    return { type: ActionType.CreatePatientFailed, errorMessage }
  },
  updatePatient(id: string, patient: IApiUpdatePatient): Action {
    return { type: ActionType.UpdatePatient, id, patient }
  },
  updatePatientBegin(): Action {
    return { type: ActionType.UpdatePatientBegin }
  },
  updatePatientSuccess(patient: IPatient): Action {
    return { type: ActionType.UpdatePatientSuccess, patient }
  },
  updatePatientFailure(): Action {
    return { type: ActionType.UpdatePatientFailure }
  },
  findPatientByMRN(mrn: string): Action {
    return { type: ActionType.FindPatientByMRN, mrn }
  },
  beginFindPatientByMRN(): Action {
    return { type: ActionType.BeginFindPatientByMRN }
  },
  findPatientByMRNSucceeded(patient: IPatient): Action {
    return { type: ActionType.FindPatientByMRNSucceeded, patient }
  },
  findPatientByMRNFailed(error: string): Action {
    return { type: ActionType.FindPatientByMRNFailed, error }
  },
  noPatientFound(): Action {
    return { type: ActionType.NoPatientFound }
  },
}

export const initialState: State = {
  list: [],
  currentPatient: undefined,
  patientAction: undefined,
  createPatientErrorMessage: '',
}

export function reducer(state: State = initialState, action: AppAction): State {
  switch (action.type) {
    case ActionType.ClearPatientContext:
      return {
        ...state,
        currentPatient: undefined,
        patientAction: undefined,
      }
    case ActionType.ClearActionStatus:
      return {
        ...state,
        patientAction: undefined,
      }
    case ActionType.ClearErrorMessage:
      return {
        ...state,
        createPatientErrorMessage: '',
      }
    case ActionType.GetList:
      return {
        ...state,
      }
    case ActionType.ListRetrieved:
      return {
        ...state,
        list: action.list,
      }
    case ActionType.GetPatient:
      return {
        ...state,
      }
    case ActionType.PatientRetrieved:
      return {
        ...state,
        currentPatient: {
          info: action.patient,
        },
      }
    case ActionType.PatientVisitsRetrieved:
      return {
        ...state,
        currentPatient: {
          ...state.currentPatient,
          visits: action.visits,
        },
      }
    case ActionType.CreatePatientSucceeded:
      return {
        ...state,
        currentPatient: {
          info: action.patient,
        },
        createPatientErrorMessage: '',
      }
    case ActionType.CreatePatientFailed:
      return {
        ...state,
        createPatientErrorMessage: action.errorMessage,
      }
    case ActionType.UpdatePatientBegin:
      return {
        ...state,
        patientAction: {
          status: PatientActionStatusId.Pending,
        },
      }
    case ActionType.UpdatePatientSuccess:
      return {
        ...state,
        currentPatient: {
          ...state.currentPatient,
          info: action.patient,
        },
        patientAction: {
          status: PatientActionStatusId.Succeeded,
        },
      }
    case ActionType.UpdatePatientFailure:
      return {
        ...state,
        patientAction: {
          status: PatientActionStatusId.Failed,
        },
      }
    case ActionType.BeginFindPatientByMRN:
      return {
        ...state,
        searchStatus: SearchStatus.PENDING,
        currentPatient: undefined,
      }
    case ActionType.FindPatientByMRNSucceeded:
      return {
        ...state,
        searchStatus: SearchStatus.FOUND,
        currentPatient: {
          info: action.patient,
        },
      }
    case ActionType.FindPatientByMRNFailed:
      return {
        ...state,
        searchStatus: SearchStatus.FAILED,
      }
    case ActionType.NoPatientFound:
      return {
        ...state,
        searchStatus: SearchStatus.NOT_FOUND,
      }
    default:
      return state
  }
}
